import { Lazy } from '../chat/lazy';
import { fromIsoString, toIsoString } from '../chat/iso-date';
import type { WorkflowRunNodeExecutionData } from './types';
import type { SerializedWorkflowRunNodeExecution } from './types';
import type { WorkflowRun } from './workflow-run';

function optionalDate(iso: string | null): Date | null {
  if (iso === null) {
    return null;
  }
  return fromIsoString(iso);
}

function optionalIso(d: Date | null): string | null {
  if (d === null) {
    return null;
  }
  return toIsoString(d);
}

export type WorkflowRunNodeExecutionHydration = {
  readonly loadRun: () => Promise<WorkflowRun | null>;
  readonly preloadedRun?: WorkflowRun | null;
};

/**
 * Per-node execution within a run (`workflow_run_node_executions`).
 */
export class WorkflowRunNodeExecution {
  private readonly _data: WorkflowRunNodeExecutionData;
  readonly run: Lazy<WorkflowRun | null>;

  private constructor(
    data: WorkflowRunNodeExecutionData,
    hydration: WorkflowRunNodeExecutionHydration
  ) {
    this._data = data;
    this.run = new Lazy(hydration.loadRun, hydration.preloadedRun);
  }

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get runId(): string {
    return this._data.runId;
  }

  get nodeId(): string {
    return this._data.nodeId;
  }

  get status(): string {
    return this._data.status;
  }

  get inputId(): string | null {
    return this._data.inputId;
  }

  get outputId(): string | null {
    return this._data.outputId;
  }

  get error(): string | null {
    return this._data.error;
  }

  get startedAt(): Date | null {
    return this._data.startedAt;
  }

  get finishedAt(): Date | null {
    return this._data.finishedAt;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get runNodeExecutionData(): WorkflowRunNodeExecutionData {
    return { ...this._data };
  }

  serialize(): SerializedWorkflowRunNodeExecution {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      runId: this._data.runId,
      nodeId: this._data.nodeId,
      status: this._data.status,
      inputId: this._data.inputId,
      outputId: this._data.outputId,
      error: this._data.error,
      startedAt: optionalIso(this._data.startedAt),
      finishedAt: optionalIso(this._data.finishedAt),
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    serialized: SerializedWorkflowRunNodeExecution,
    hydration: WorkflowRunNodeExecutionHydration
  ): WorkflowRunNodeExecution {
    const data: WorkflowRunNodeExecutionData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      runId: serialized.runId,
      nodeId: serialized.nodeId,
      status: serialized.status,
      inputId: serialized.inputId,
      outputId: serialized.outputId,
      error: serialized.error,
      startedAt: optionalDate(serialized.startedAt),
      finishedAt: optionalDate(serialized.finishedAt),
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new WorkflowRunNodeExecution(data, hydration);
  }
}

export async function resolveNoRun(): Promise<WorkflowRun | null> {
  return null;
}
